import { useState, useMemo, useEffect, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserRole, AdminUserResponse, DriverProfileResponse, FleetManagerProfileResponse, ShipperProfileResponse } from '../../api/types';
import { createUser, updateUser, adminUpdateFleetManagerProfile, adminUpdateShipperProfile, adminUpdateDriverProfile, adminUploadProfileDocument } from '../../api/endpoints/admin';
import { useMutationHook } from '../../hooks/useMutationHook';
import { INDIAN_STATES } from '../../config/indianStates';
import { MultiSearchableSelect } from '../ui/MultiSearchableSelect';
import styles from './UserFormModal.module.css';
import { DatePicker } from '../ui/DatePicker';
import { DialogShell } from '../ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../ui/dialogDismiss';
import { PhoneInput } from '../ui/PhoneInput';
import { isPhoneInvalidIndia } from '../../api/dnmFailure';
import { INDIAN_MOBILE_ERROR, isValidNewIndianMobile } from '../../utils/phone';
import formStyles from './FormModal.module.css';
import { DialogHelp } from '../../help/DialogHelp';

const ROLE_LABEL: Record<string, string> = {
  DRIVER: 'driver',
  SHIPPER: 'shipper',
  FLEET_MANAGER: 'partner',
};

const STATE_OPTIONS = INDIAN_STATES.map((s) => ({
  id: s.id,
  label: s.name,
  subtitle: s.capital,
}));

interface Props {
  role: Exclude<UserRole, 'ADMIN'>;
  /** When set, the modal edits this user (phone is immutable) */
  user?: AdminUserResponse | null;
  /** When editing a fleet manager or shipper, the current profile details */
  initialProfile?: DriverProfileResponse | FleetManagerProfileResponse | ShipperProfileResponse | null;
  initialFocusId?: string;
  /** Create only: prefill from the text typed in a picker (ignored when `user` is set). */
  initialValues?: { fullName?: string; phone?: string; companyName?: string };
  onClose: () => void;
  onSaved?: (user: AdminUserResponse) => void;
}

/**
 * Add / Edit user modal. Supports role-specific details (company name, GSTIN,
 * contact numbers, billing address, and service regions for Fleet Managers).
 */
export function UserFormModal({ role, user, initialProfile, initialValues, onClose, onSaved, initialFocusId }: Props) {
  const { t } = useTranslation();
  useEffect(() => {
    if (initialFocusId) document.getElementById(initialFocusId)?.focus();
  }, [initialFocusId]);
  const [createdUser, setCreatedUser] = useState<AdminUserResponse | null>(null);
  const targetUser = user ?? createdUser;
  const isEdit = !!targetUser;
  const isFm = role === 'FLEET_MANAGER';
  const isShipper = role === 'SHIPPER';

  const driverProfile = role === 'DRIVER' ? initialProfile as DriverProfileResponse | null : null;
  const [licenseNumber, setLicenseNumber] = useState(driverProfile?.licenseNumber ?? '');
  const [licenseExpiry, setLicenseExpiry] = useState(driverProfile?.licenseExpiry ?? '');
  const [vehicleNumber, setVehicleNumber] = useState(driverProfile?.vehicleNumber ?? '');
  const [files, setFiles] = useState<Record<string, File>>({});
  const prefill = user ? undefined : initialValues;
  const [fullName, setFullName] = useState(user?.fullName ?? prefill?.fullName ?? '');
  /** 10 national digits for a new user; the stored number is shown read-only when editing. */
  const [phone, setPhone] = useState(prefill?.phone ?? '');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  // Focusing the field scrolls it into view, so its error is seen even when the
  // modal is scrolled down to the buttons (1200 px @125%).
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState(user?.email ?? '');

  const fmProfile = isFm ? (initialProfile as FleetManagerProfileResponse | null) : null;
  const shipperProfile = isShipper ? (initialProfile as ShipperProfileResponse | null) : null;

  const [companyName, setCompanyName] = useState(
    fmProfile?.companyName ?? shipperProfile?.companyName ?? prefill?.companyName ?? '',
  );
  const [secondaryPhone, setSecondaryPhone] = useState(
    fmProfile?.secondaryPhoneNumber ?? shipperProfile?.secondaryPhoneNumber ?? '',
  );
  const [whatsappPhone, setWhatsappPhone] = useState(
    fmProfile?.whatsappNumber ?? shipperProfile?.whatsappNumber ?? '',
  );
  const [billingAddress, setBillingAddress] = useState(
    fmProfile?.billingAddress ??
      (typeof shipperProfile?.billingAddress === 'string'
        ? shipperProfile.billingAddress
        : shipperProfile?.billingAddressText ??
          (shipperProfile?.billingAddress
            ? `${shipperProfile.billingAddress.line}, ${shipperProfile.billingAddress.city}, ${shipperProfile.billingAddress.state} - ${shipperProfile.billingAddress.pincode}`
            : '')),
  );
  const [gstin, setGstin] = useState(fmProfile?.gstin ?? shipperProfile?.gstin ?? '');
  const [panNumber, setPanNumber] = useState(fmProfile?.panNumber ?? shipperProfile?.panNumber ?? '');
  const [fleetSize, setFleetSize] = useState<string>(
    fmProfile?.fleetSize != null ? String(fmProfile.fleetSize) : '',
  );

  // Banking fields for Fleet Manager
  const [bankName, setBankName] = useState(fmProfile?.bankName ?? '');
  const [accountHolderName, setAccountHolderName] = useState(fmProfile?.accountHolderName ?? '');
  const [accountNumber, setAccountNumber] = useState(fmProfile?.accountNumber ?? '');
  const [ifscCode, setIfscCode] = useState(fmProfile?.ifscCode ?? '');
  const [bankProofType, setBankProofType] = useState<string>(fmProfile?.bankProofType ?? 'CHEQUE');
  const [bankProofUrl] = useState(fmProfile?.bankProofUrl ?? '');

  // Parse initial service regions
  const initialRegions = useMemo(() => {
    if (!fmProfile?.serviceRegions) return [];
    return fmProfile.serviceRegions
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((item) => {
        const found = INDIAN_STATES.find(
          (st) => st.id.toLowerCase() === item.toLowerCase() || st.name.toLowerCase() === item.toLowerCase(),
        );
        return found ? found.id : item;
      });
  }, [fmProfile?.serviceRegions]);

  const [serviceRegions, setServiceRegions] = useState<string[]>(initialRegions);
  const [error, setError] = useState<string | null>(null);

  const label = ROLE_LABEL[role] ?? 'user';

  const saveMut = useMutationHook(
    async () => {
      const regionsStr =
        isFm && serviceRegions.length > 0
          ? serviceRegions
            .map((id) => INDIAN_STATES.find((st) => st.id === id)?.name ?? id)
            .join(', ')
          : undefined;

      const fleetSizeNum =
        isFm && fleetSize.trim() !== ''
          ? parseInt(fleetSize.trim(), 10)
          : undefined;

      const gstinVal =
        (isFm || isShipper) && gstin.trim() !== ''
          ? gstin.trim().toUpperCase()
          : undefined;

      const panVal =
        (isFm || isShipper) && panNumber.trim() !== ''
          ? panNumber.trim().toUpperCase()
          : undefined;

      const companyVal =
        (isFm || isShipper) && companyName.trim() !== ''
          ? companyName.trim()
          : undefined;

      const secPhoneVal =
        (isFm || isShipper) && secondaryPhone.trim() !== ''
          ? secondaryPhone.trim()
          : undefined;

      const whatsappVal =
        (isFm || isShipper) && whatsappPhone.trim() !== ''
          ? whatsappPhone.trim()
          : undefined;

      const billingAddrVal =
        (isFm || isShipper) && billingAddress.trim() !== ''
          ? billingAddress.trim()
          : undefined;

      const userRes = isEdit
        ? await updateUser(targetUser!.id, {
          fullName: fullName.trim() || undefined,
          email: email.trim() || undefined,
          companyName: companyVal,
          gstin: gstinVal,
          serviceRegions: regionsStr,
          fleetSize: fleetSizeNum,
          secondaryPhoneNumber: secPhoneVal,
          whatsappNumber: whatsappVal,
          billingAddress: billingAddrVal,
        })
        : await createUser({
          role,
          phoneNumber: `+91${phone}`,
          fullName: fullName.trim(),
          email: email.trim() || undefined,
          companyName: companyVal,
          gstin: gstinVal,
          serviceRegions: regionsStr,
          fleetSize: fleetSizeNum,
          secondaryPhoneNumber: secPhoneVal,
          whatsappNumber: whatsappVal,
          billingAddress: billingAddrVal,
        });

      if (!targetUser) setCreatedUser(userRes);
      const uploaded: Record<string, string> = {};
      for (const [tag, file] of Object.entries(files)) {
        uploaded[tag] = (await adminUploadProfileDocument(userRes.id, file, tag)).fileUrl;
      }
      if (isFm) {
        await adminUpdateFleetManagerProfile(userRes.id, {
          companyName: companyVal,
          gstin: gstinVal,
          serviceRegions: regionsStr,
          fleetSize: fleetSizeNum,
          secondaryPhoneNumber: secPhoneVal,
          whatsappNumber: whatsappVal,
          billingAddress: billingAddrVal,
          panNumber: panVal,
          bankName: bankName.trim() || undefined,
          accountHolderName: accountHolderName.trim() || undefined,
          accountNumber: accountNumber.trim() || undefined,
          ifscCode: ifscCode.trim().toUpperCase() || undefined,
          bankProofType: bankProofType.trim() || undefined,
          bankProofUrl: uploaded.BANK_PROOF ?? (bankProofUrl.trim() || undefined),
          panDocumentUrl: uploaded.PAN,
          gstDocumentUrl: uploaded.GST,
        });
      }

      if (isShipper) {
        await adminUpdateShipperProfile(userRes.id, {
          companyName: companyVal,
          gstin: gstinVal,
          panNumber: panVal,
          secondaryPhoneNumber: secPhoneVal,
          whatsappNumber: whatsappVal,
          billingAddressText: billingAddrVal,
          panDocumentUrl: uploaded.PAN,
          gstDocumentUrl: uploaded.GST,
        });
      }

      if (role === 'DRIVER') {
        await adminUpdateDriverProfile(userRes.id, {
          vehicleType: driverProfile?.vehicleType ?? 'LIGHT_TRUCK',
          vehicleNumber: vehicleNumber.trim().toUpperCase() || undefined,
          licenseNumber: licenseNumber.trim() || undefined,
          licenseExpiry: licenseExpiry || undefined,
          dlDocumentUrl: uploaded.DL,
          rcDocumentUrl: uploaded.RC,
        });
      }
      return userRes;
    },
    {
      successMessage: isEdit
        ? 'User details updated.'
        : `New ${label} created — they can log into the mobile app with phone OTP.`,
      invalidateKeys: [
        ['admin', 'users'],
        ['admin', 'dashboard'],
        ...(isEdit && isFm ? [['payments']] : []),
        ...(user ? [['admin', 'users', 'overview', user.id], ['admin', 'users', user.id], ['admin', 'users', 'detail', user.id]] : []),
      ],
      errorMessage: (err) => (isPhoneInvalidIndia(err) ? INDIAN_MOBILE_ERROR : err.message),
      onSuccess: (saved) => {
        onSaved?.(saved);
        onClose();
      },
      onError: (err) => {
        if (isPhoneInvalidIndia(err)) {
          setPhoneError(INDIAN_MOBILE_ERROR);
          phoneInputRef.current?.focus();
        }
      },
    },
  );

  const validate = (): string | null => {
    if (!isEdit && !fullName.trim()) return 'Full name is required.';
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim()))
      return 'Please enter a valid email address.';

    if (isFm || isShipper) {
      if (secondaryPhone.trim() && !/^\+[0-9]{8,15}$/.test(secondaryPhone.trim())) {
        return 'Secondary phone must be in E.164 format, e.g. +919876543210.';
      }
      if (whatsappPhone.trim() && !/^\+[0-9]{8,15}$/.test(whatsappPhone.trim())) {
        return 'WhatsApp number must be in E.164 format, e.g. +919876543210.';
      }
      if (
        gstin.trim() &&
        !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin.trim().toUpperCase())
      ) {
        return 'Please enter a valid 15-character GSTIN (e.g. 27AAPFU0939F1ZV).';
      }
      if (
        panNumber.trim() &&
        !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(panNumber.trim().toUpperCase())
      ) {
        return 'Please enter a valid 10-character PAN (e.g. ABCDE1234F).';
      }
    }

    if (isFm) {
      if (!isEdit && !companyName.trim()) {
        return 'Company name is required for partners.';
      }
      if (fleetSize.trim() && (isNaN(Number(fleetSize)) || Number(fleetSize) < 0)) {
        return 'Fleet size must be a non-negative number.';
      }
      if (
        ifscCode.trim() &&
        !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode.trim().toUpperCase())
      ) {
        return 'Please enter a valid 11-character IFSC code (e.g. HDFC0000123).';
      }
    }
    return null;
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const phoneProblem = !isEdit && !isValidNewIndianMobile(phone) ? INDIAN_MOBILE_ERROR : null;
    setPhoneError(phoneProblem);
    const problem = validate();
    if (phoneProblem) {
      setError(problem);
      phoneInputRef.current?.focus();
      return;
    }
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  const title = isEdit ? `Edit ${label}` : `Add ${label}`;
  const dirty = useIsDirty({
    licenseNumber, licenseExpiry, vehicleNumber, files, fullName, phone, email, companyName,
    secondaryPhone, whatsappPhone, billingAddress, gstin, panNumber, fleetSize, bankName,
    accountHolderName, accountNumber, ifscCode, bankProofType, serviceRegions,
  });
  const dismiss = useDialogDismiss({ dirty, busy: saveMut.isLoading, onClose });

  return (
    <DialogShell dismiss={dismiss} label={title} scrimClassName={styles.scrim} panelClassName={styles.panel}>
      <div className={formStyles.titleRow}>
        <h2 className={styles.title}>{title}</h2>
        <DialogHelp context="user-form" title={title} />
      </div>

      <form onSubmit={submit}>
        <label className={styles.label}>
          {t('domain.userFormModal.fullNameLabel')}{' '}{!isEdit && <span className={styles.required}>*</span>}
        </label>
        <input
          className={styles.input}
          autoFocus
          id="profile-full-name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder={t('domain.userFormModal.fullNamePlaceholder')}
        />

        <label className={styles.label} htmlFor="profile-phone">
          {t('domain.userFormModal.phoneLabel')}{' '}{isEdit ? '(cannot be changed)' : <span className={styles.required}>*</span>}
        </label>
        {isEdit ? (
          <input
            id="profile-phone"
            className={styles.input}
            type="tel"
            value={targetUser?.phoneNumber ?? ''}
            disabled
          />
        ) : (
          <PhoneInput
            ref={phoneInputRef}
            id="profile-phone"
            value={phone}
            onValueChange={(next) => {
              setPhone(next);
              setPhoneError(null);
            }}
            error={phoneError}
            placeholder={t('domain.userFormModal.phonePlaceholder')}
          />
        )}

        <label className={styles.label}>{t('domain.userFormModal.emailLabel')}</label>
        <input
          className={styles.input}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('domain.userFormModal.emailPlaceholder')}
        />

        {(isFm || isShipper) && (
          <>
            <div className={styles.sectionHeading}>{t('domain.userFormModal.businessHeading')}</div>

            <label className={styles.label}>
              {t('domain.userFormModal.companyNameLabel')}{' '}{isFm && !isEdit ? <span className={styles.required}>*</span> : '(optional)'}
            </label>
            <input
              className={styles.input}
              id="profile-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={isFm ? t('domain.userFormModal.companyNamePlaceholderFm') : t('domain.userFormModal.companyNamePlaceholderShipper')}
            />

            <div className={styles.fieldRow}>
              <div className={styles.fieldCol}>
                <label className={styles.label}>{t('domain.userFormModal.secondaryPhoneLabel')}</label>
                <input
                  className={styles.input}
                  type="tel"
                  value={secondaryPhone}
                  onChange={(e) => setSecondaryPhone(e.target.value)}
                  placeholder={t('domain.userFormModal.phoneE164Placeholder')}
                />
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.label}>{t('domain.userFormModal.whatsappLabel')}</label>
                <input
                  className={styles.input}
                  type="tel"
                  value={whatsappPhone}
                  onChange={(e) => setWhatsappPhone(e.target.value)}
                  placeholder={t('domain.userFormModal.phoneE164Placeholder')}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldCol}>
                <label className={styles.label} htmlFor="profile-gstin">{t('domain.userFormModal.gstinLabel')}</label>
                <input
                  className={styles.input}
                  id="profile-gstin"
                  value={gstin}
                  maxLength={15}
                  onChange={(e) => setGstin(e.target.value.toUpperCase())}
                  placeholder={t('domain.userFormModal.gstinPlaceholder')}
                />
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.label} htmlFor="profile-pan">{t('domain.userFormModal.panLabel')}</label>
                <input
                  className={styles.input}
                  id="profile-pan"
                  value={panNumber}
                  maxLength={10}
                  onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                  placeholder={t('domain.userFormModal.panPlaceholder')}
                />
              </div>
            </div>
            {isFm && (
              <div style={{ marginBottom: '12px' }}>
                <label className={styles.label}>{t('domain.userFormModal.fleetSizeLabel')}</label>
                <input
                  className={styles.input}
                  type="number"
                  min="0"
                  value={fleetSize}
                  onChange={(e) => setFleetSize(e.target.value)}
                  placeholder={t('domain.userFormModal.fleetSizePlaceholder')}
                />
              </div>
            )}

            {isFm && (
              <>
                <div className={styles.sectionHeading} style={{ marginTop: '16px' }}>{t('domain.userFormModal.bankingHeading')}</div>
                <div className={styles.fieldRow}>
                  <div className={styles.fieldCol}>
                    <label className={styles.label}>{t('domain.userFormModal.bankNameLabel')}</label>
                    <input
                      className={styles.input}
                      id="profile-bank-name"
                  value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder={t('domain.userFormModal.bankNamePlaceholder')}
                    />
                  </div>
                  <div className={styles.fieldCol}>
                    <label className={styles.label}>{t('domain.userFormModal.accountHolderLabel')}</label>
                    <input
                      className={styles.input}
                      value={accountHolderName}
                      onChange={(e) => setAccountHolderName(e.target.value)}
                      placeholder={t('domain.userFormModal.accountHolderPlaceholder')}
                    />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.fieldCol}>
                    <label className={styles.label}>{t('domain.userFormModal.accountNumberLabel')}</label>
                    <input
                      className={styles.input}
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder={t('domain.userFormModal.accountNumberPlaceholder')}
                    />
                  </div>
                  <div className={styles.fieldCol}>
                    <label className={styles.label}>{t('domain.userFormModal.ifscLabel')}</label>
                    <input
                      className={styles.input}
                      value={ifscCode}
                      maxLength={11}
                      onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                      placeholder={t('domain.userFormModal.ifscPlaceholder')}
                    />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.fieldCol}>
                    <label className={styles.label}>{t('domain.userFormModal.bankProofTypeLabel')}</label>
                    <select
                      className={styles.input}
                      value={bankProofType}
                      onChange={(e) => setBankProofType(e.target.value)}
                    >
                      <option value="CHEQUE">{t('domain.userFormModal.bankProofCheque')}</option>
                      <option value="PASSBOOK">{t('domain.userFormModal.bankProofPassbook')}</option>
                    </select>
                  </div>

                </div>
              </>
            )}

            <label className={styles.label}>{t('domain.userFormModal.billingAddressLabel')}</label>
            <textarea
              className={styles.input}
              rows={2}
              style={{ resize: 'vertical' }}
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              placeholder={t('domain.userFormModal.billingAddressPlaceholder')}
            />

            {isFm && (
              <>
                <label className={styles.label}>{t('domain.userFormModal.serviceRegionsLabel')}</label>
                <MultiSearchableSelect
                  options={STATE_OPTIONS}
                  selectedValues={serviceRegions}
                  onChange={setServiceRegions}
                  placeholder={t('domain.userFormModal.serviceRegionsPlaceholder')}
                />
              </>
            )}
          </>
        )}

        {role === 'DRIVER' && (
          <section>
            <div className={styles.sectionHeading}>{t('domain.userFormModal.driverHeading')}</div>
            <label className={styles.label} htmlFor="driver-license">{t('domain.userFormModal.dlNumberLabel')}</label>
            <input id="driver-license" className={styles.input} value={licenseNumber} onChange={e => setLicenseNumber(e.target.value)} />
            <label className={styles.label} htmlFor="driver-expiry">{t('domain.userFormModal.dlExpiryLabel')}</label>
            <DatePicker id="driver-expiry" className={styles.input} value={licenseExpiry} onChange={setLicenseExpiry} />
            <label className={styles.label} htmlFor="driver-truck">{t('domain.userFormModal.truckNumberLabel')}</label>
            <input id="driver-truck" className={styles.input} value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value.toUpperCase())} />
          </section>
        )}
        <div className={styles.sectionHeading}>{t('domain.userFormModal.documentsHeading')}</div>
        {(role === 'DRIVER' ? ['DL', 'RC'] : isFm ? ['PAN', 'GST', 'BANK_PROOF'] : ['PAN', 'GST']).map(tag => (
          <div key={tag} className={styles.fieldCol}>
            <label className={styles.label} htmlFor={`document-${tag}`}>{tag === 'BANK_PROOF' ? 'Bank proof document' : `${tag} document`}</label>
            <input id={`document-${tag}`} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" disabled={saveMut.isLoading}
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 10 * 1024 * 1024) { setError('Documents must be 10 MB or smaller.'); e.target.value = ''; return; }
                setFiles(previous => ({ ...previous, [tag]: file }));
              }} />
          </div>
        ))}
        <p className={styles.hint}>{t('domain.userFormModal.documentsHint')}</p>

        {!isEdit && (
          <p className={styles.hint}>
            {t('domain.userFormModal.otpHint', { label })}
          </p>
        )}

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={dismiss.requestClose}>
            {t('common.actions.cancel')}
          </button>
          <button type="submit" className={styles.submit} disabled={saveMut.isLoading}>
            {saveMut.isLoading ? 'Saving…' : isEdit ? 'Save changes' : `Create ${label}`}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
